import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, ExternalLink, Navigation, Bookmark, Phone, AlertTriangle, Trash2 } from 'lucide-react';
import itemTypeIcons from './itemTypeIcons';
import LikeButton from '../../../components/LikeButton/LikeButton';
import { Button } from '../../../components/ui/button';

const FavoriteResultCard = ({
  title,
  description,
  additionalInfo,
  postId,
  imageUrl,
  itemType,
  isDeleted = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultTitle = title || t("untitled_favorite", "Untitled");
  const defaultDescription = description || t("no_description_available", "No description available");
  const IconComponent = itemTypeIcons[itemType] || Bookmark;

  // Extract links from additionalInfo
  const addressLink = additionalInfo?.AddressLink;
  const websiteLink = additionalInfo?.WebsiteLink;
  const address = additionalInfo?.Address;
  const phone = additionalInfo?.Phone;
  
  // Filter out link entries for the info list
  const infoEntries = Object.entries(additionalInfo || {}).filter(
    ([key]) => !['AddressLink', 'WebsiteLink', 'Address', 'Website', 'Phone'].includes(key)
  );

  const isClickable = !isDeleted && itemType !== 'Place';

  const handleCardClick = () => {
    if (isClickable && postId) {
      navigate(`/post/${postId}`, {
        state: { postType: itemType },
      });
    }
  };

  if (isDeleted) {
    return (
      <div className="bg-white rounded-xl border border-border overflow-hidden shadow-sm opacity-75">
        <div className="flex">
          {/* Icon Section */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 flex-shrink-0 bg-muted/50 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-muted-foreground/50" />
          </div>

          {/* Content Section */}
          <div className="flex-1 p-2.5 sm:p-3 md:p-4 min-w-0">
            <div className="flex items-start justify-between gap-1.5 sm:gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-muted-foreground text-xs sm:text-sm md:text-base">
                  {defaultTitle}
                </h3>
                <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground/70 line-clamp-2 mt-0.5">
                  {defaultDescription}
                </p>
              </div>
              
              {/* Remove Button */}
              <div className="flex-shrink-0">
                <LikeButton postId={postId} itemType={itemType} />
              </div>
            </div>

            {/* Helper text */}
            <div className="flex items-center gap-1.5 mt-2 sm:mt-3">
              <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground/50" />
              <span className="text-[10px] sm:text-xs text-muted-foreground/70">
                {t("tap_bookmark_to_remove", "Tap bookmark to remove from saved")}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <div className="flex">
        {/* Image / Icon Section */}
        <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 flex-shrink-0 bg-secondary/30 flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={defaultTitle}
              className="w-full h-full object-cover"
            />
          ) : (
            <IconComponent className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary/60" />
          )}
        </div>

        {/* Content Section */}
        <div className="flex-1 p-2.5 sm:p-3 md:p-4 min-w-0">
          <div className="flex items-start justify-between gap-1.5 sm:gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground text-xs sm:text-sm md:text-base truncate">
                {defaultTitle}
              </h3>
              <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground line-clamp-2 mt-0.5">
                {defaultDescription}
              </p>
            </div>
            
            {/* Like/Save Button - stop propagation to prevent card click */}
            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <LikeButton postId={postId} itemType={itemType} />
            </div>
          </div>

          {/* Address if available */}
          {address && (
            <div className="flex items-start gap-1 sm:gap-1.5 mt-1.5 sm:mt-2">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <span className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{address}</span>
            </div>
          )}

          {/* Additional Info Tags */}
          {infoEntries.length > 0 && (
            <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-1.5 sm:mt-2">
              {infoEntries.slice(0, 3).map(([key, value]) => (
                value && (
                  <span 
                    key={key}
                    className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full bg-secondary text-[10px] sm:text-xs text-foreground"
                  >
                    <span className="font-medium">{key}:</span>
                    <span className="ml-1 text-muted-foreground truncate max-w-[60px] sm:max-w-none">{value}</span>
                  </span>
                )
              ))}
            </div>
          )}

          {/* Action Buttons */}
          {(addressLink || websiteLink || phone) && (
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3" onClick={(e) => e.stopPropagation()}>
              {addressLink && (
                <Button
                  size="sm"
                  className="h-7 sm:h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs bg-primary hover:bg-primary/90 text-primary-foreground gap-1 sm:gap-1.5"
                  onClick={() => window.open(addressLink, "_blank")}
                >
                  <Navigation className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  {t("directions", "Directions")}
                </Button>
              )}
              {phone && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 sm:h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs gap-1 sm:gap-1.5"
                  asChild
                >
                  <a href={`tel:${phone}`}>
                    <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    {t("call", "Call")}
                  </a>
                </Button>
              )}
              {websiteLink && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 sm:h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs gap-1 sm:gap-1.5 border-primary/30 hover:bg-primary/5"
                  onClick={() => window.open(websiteLink, "_blank")}
                >
                  <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  {t("visit_website", "Visit Website")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(FavoriteResultCard);
